// Catálogos que llena el formulario de promo: productos y tiendas de la cadena, y segmentos de
// retail-promotions. Solo se piden con el formulario abierto.
import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import {
    listProducts,
    type CatalogProduct,
} from '@/api/services/retail-catalog/retail-catalog.client';
import { listStores, type Store } from '@/api/services/retail-org/retail-org.client';
import { resolveInventoryRetailer } from '@/domains/retail/admin/inventario/lib/inventoryRetailer';
import { useAuth } from '@/hooks/useAuth';
import type { PromotionsAvailability } from '../lib/promotionsFormat';
import type { SalePromotionSegment } from './promotionsContract';
import { useSalePromotionSegments } from './useSalePromotionSegments';
import { PROMOTIONS_QUERY_KEY } from './usePromotions';

export type SourceState = 'loading' | 'ready' | 'unavailable';

export interface PromotionFormSources {
    products: CatalogProduct[];
    productsState: SourceState;
    stores: Store[];
    storesState: SourceState;
    /** Elegibles como destino: sin el catch-all del tenant. */
    segments: SalePromotionSegment[];
    segmentsState: PromotionsAvailability;
}

function stateOf(
    query: { isSuccess: boolean; isError: boolean },
    hasRetailer: boolean,
): SourceState {
    if (!hasRetailer || query.isError) return 'unavailable';
    return query.isSuccess ? 'ready' : 'loading';
}

function useRetailerId(): string | null {
    const { roleAssignments } = useAuth();
    return useMemo(() => resolveInventoryRetailer(roleAssignments).retailerId, [roleAssignments]);
}

function useChainStores(retailerId: string | null, enabled: boolean) {
    return useQuery({
        queryKey: [...PROMOTIONS_QUERY_KEY, 'stores', retailerId],
        queryFn: () => listStores(retailerId ?? ''),
        enabled: enabled && retailerId !== null,
        staleTime: 300_000,
    });
}

/** Nombre de cada tienda de la cadena por id; vacío mientras carga o si no se puede leer. */
export function useChainStoreNames(enabled: boolean): Map<string, string> {
    const stores = useChainStores(useRetailerId(), enabled);
    return useMemo(
        () => new Map((stores.data ?? []).map((store) => [store.storeId, store.name])),
        [stores.data],
    );
}

/** `chosenRetailerId` manda sobre la cadena del usuario: la pasa quien elige cadena en pantalla. */
export function usePromotionFormSources(
    enabled: boolean,
    chosenRetailerId?: string,
): PromotionFormSources {
    const ownRetailerId = useRetailerId();
    const retailerId = chosenRetailerId ?? ownRetailerId;
    const active = enabled && retailerId !== null;
    const products = useQuery({
        queryKey: [...PROMOTIONS_QUERY_KEY, 'products', retailerId],
        queryFn: () => listProducts(retailerId ?? ''),
        enabled: active,
        staleTime: 300_000,
    });
    const stores = useChainStores(retailerId, enabled);
    const segments = useSalePromotionSegments(enabled);
    return {
        products: products.data ?? [],
        productsState: stateOf(products, retailerId !== null),
        stores: stores.data ?? [],
        storesState: stateOf(stores, retailerId !== null),
        segments: segments.segments,
        segmentsState: segments.state,
    };
}
